import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, Input } from '@ui';
import { X, ChevronDown } from '@ui/icons';
import { cn, BREAKDOWN_CHARGE_NAMES, chargeSignMode, formatDecimalWithCommas } from '@shared/lib';
import { toast } from '@shared/lib/toast';
import { t } from '@shared/i18n';
import type { SplitMember } from './SplitModal';
import { SplitChoiceModal } from './SplitChoiceModal';
import { AssignItemsModal } from './AssignItemsModal';
import {
  computeAssignedShares,
  signedChargeAmount,
  type AssignedShare,
} from '../model/assignItems';

export interface BreakdownItem {
  id: string;
  name: string;
  price: string;
  /** A charge's chosen sign — only meaningful for a free-sign charge ("Other", see
   *  `chargeSignMode`), where the operator box is a toggle. Absent ⇒ "+". */
  sign?: '+' | '-';
}

/** Keep at most 2 decimal places while typing (e.g. "5.444" → "5.44"); a leading "−" is kept for
 *  discount fees. Partial decimals ("5." / "-" ) keep typing normally. */
const to2dp = (v: string): string => {
  const m = /^-?\d*\.?\d{0,2}/.exec(v);
  return m ? m[0] : v;
};

/** The operator shown for a charge row: fixed by role for Discount (−) / Tip / Tax (+); for a
 *  free-sign charge ("Other") it is the row's own toggled `sign`. */
const feeOperator = (fee: BreakdownItem): '+' | '-' => {
  switch (chargeSignMode(fee.name)) {
    case 'minus':
      return '-';
    case 'plus':
      return '+';
    default:
      return fee.sign ?? '+';
  }
};

export interface BreakdownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialItems: BreakdownItem[];
  initialFeesAndTaxes: BreakdownItem[];
  currencySymbol: string;
  receiptAmount: string;
  members: SplitMember[];
  /**
   * `assignedShares` is the outcome of the split-choice step that follows an itemized Save:
   *  • a per-member map — the user ASSIGNED items to people (`computeAssignedShares`); the bill
   *    should split by those amounts among exactly those members;
   *  • `null` — the user chose "Split total (evenly)"; any earlier item-assignment split is void;
   *  • `undefined` — no split choice was made (no items, saved directly); leave the split alone.
   */
  onSave: (
    items: BreakdownItem[],
    feesAndTaxes: BreakdownItem[],
    assignedShares?: Record<string, AssignedShare> | null,
  ) => void;
}

export function BreakdownModal({
  open,
  onOpenChange,
  initialItems,
  initialFeesAndTaxes,
  currencySymbol,
  receiptAmount,
  members,
  onSave,
}: BreakdownModalProps) {
  const [items, setItems] = useState<BreakdownItem[]>(() =>
    initialItems.length > 0 ? initialItems : [{ id: 'init-item-1', name: '', price: '' }],
  );
  const [feesAndTaxes, setFeesAndTaxes] = useState<BreakdownItem[]>(initialFeesAndTaxes);

  // Split flow modals
  const [splitChoiceOpen, setSplitChoiceOpen] = useState(false);
  const [assignItemsOpen, setAssignItemsOpen] = useState(false);

  // Snapshot of validated items/fees passed to child modals
  const [pendingItems, setPendingItems] = useState<BreakdownItem[]>([]);
  const [pendingFees, setPendingFees] = useState<BreakdownItem[]>([]);

  const addItem = () => {
    setItems([...items, { id: `item-${Date.now()}-${Math.random()}`, name: '', price: '' }]);
  };

  const removeItem = (id: string) => {
    setItems(items.filter((item) => item.id !== id));
  };

  const updateItem = (id: string, field: 'name' | 'price', value: string) => {
    const v = field === 'price' ? to2dp(value) : value;
    setItems(items.map((item) => (item.id === id ? { ...item, [field]: v } : item)));
  };

  const addFee = () => {
    // Rows default to Tip → Discount → Tax → Other in order; every row after that defaults to
    // Other (there's no fifth distinct default to cycle to). Same list the read-back mappers use to
    // recognise a saved charge (`classifyBreakdownRow`), so the two can't drift apart.
    const defaultName = BREAKDOWN_CHARGE_NAMES[feesAndTaxes.length] ?? 'Other';
    setFeesAndTaxes([
      ...feesAndTaxes,
      { id: `fee-${Date.now()}-${Math.random()}`, name: defaultName, price: '' },
    ]);
  };

  const removeFee = (id: string) => {
    setFeesAndTaxes(feesAndTaxes.filter((fee) => fee.id !== id));
  };

  const updateFee = (id: string, field: 'name' | 'price' | 'sign', value: string) => {
    const v = field === 'price' ? to2dp(value) : value;
    setFeesAndTaxes(feesAndTaxes.map((fee) => (fee.id === id ? { ...fee, [field]: v } : fee)));
  };

  const handleSave = () => {
    // Filter out completely blank rows
    const activeItems = items.filter((i) => i.name.trim() !== '' || i.price.trim() !== '');
    const activeFees = feesAndTaxes.filter((f) => f.name.trim() !== '' || f.price.trim() !== '');

    // Every item that carries a value must be named — block Save with a toast when one has an amount
    // (or is otherwise active) but no name.
    if (activeItems.some((i) => i.name.trim() === '')) {
      toast.error(t.newBill.validation.itemNameRequired);
      return;
    }

    if (activeItems.length > 0) {
      // Save snapshot and open the split-choice dialog
      setPendingItems(activeItems);
      setPendingFees(activeFees);
      setSplitChoiceOpen(true);
    } else {
      // No items — just save directly
      onSave(activeItems, activeFees);
      onOpenChange(false);
    }
  };

  /** User chose "Split total (evenly)" — persist items and close; `null` voids any earlier
   *  item-assignment split so the bill goes back to its plain split. */
  const handleSplitTotal = () => {
    onSave(pendingItems, pendingFees, null);
    onOpenChange(false);
  };

  /** User chose "Assign items to people" — open assign modal. */
  const handleOpenAssign = () => {
    setAssignItemsOpen(true);
  };

  // Calculations
  const receiptVal = parseFloat(receiptAmount) || 0;
  const itemizedSum =
    items.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0) +
    feesAndTaxes.reduce((sum, fee) => sum + signedChargeAmount(fee.name, fee.price, fee.sign), 0);
  // Remaining = itemized − receipt: OVER-itemized shows POSITIVE (excess), UNDER shows NEGATIVE, and
  // an exact match is 0 (balanced). (Display-only; the save guard compares totals separately.)
  const remainingVal = itemizedSum - receiptVal;
  // Reconcile status (mobile parity): within a 0.001 tolerance the receipt is MATCHED (green);
  // itemized OVER the receipt "Exceeds receipt amount" (red, +); itemized UNDER leaves a "Remaining
  // amount" (orange, −). Save is only allowed when matched.
  const absRemaining = Math.abs(remainingVal);
  const isMatched = absRemaining <= 0.001;
  // Mobile parity: the breakdown may be SAVED while the itemized total is within ±0.90 of the
  // receipt (rounding slack) — the caller then updates the bill amount to the itemized total
  // (e.g. a 100.00 receipt itemized to 99.10 saves and the expense becomes 99.10). The
  // over/under status labels above still show the difference; only saving is tolerance-gated.
  // Compared in whole cents so float artifacts (0.9000000000000909) can't flip the gate.
  const canSave = Math.round(absRemaining * 100) <= 90;
  const breakdownStatus = (() => {
    if (isMatched) {
      return {
        label: t.newBill.breakdownStatus.matched,
        colorClass: 'text-positive',
        value: `${currencySymbol}${formatDecimalWithCommas((0).toFixed(2))}`,
      };
    }
    if (remainingVal > 0) {
      return {
        label: t.newBill.breakdownStatus.exceeds,
        colorClass: 'text-danger',
        value: `+${currencySymbol}${formatDecimalWithCommas(absRemaining.toFixed(2))}`,
      };
    }
    return {
      label: t.newBill.breakdownStatus.remaining,
      colorClass: 'text-[#B27223]',
      value: `-${currencySymbol}${formatDecimalWithCommas(absRemaining.toFixed(2))}`,
    };
  })();

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50" />
          <Dialog.Content
            aria-describedby={undefined}
            className={cn(
              'fixed left-1/2 top-1/2 z-60 flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-[800px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface-raised shadow-overlay focus:outline-none',
            )}
          >
            {/* Header */}
            <div className="flex items-center gap-4 border-b border-border bg-header-bar px-5 py-2">
              <Dialog.Title className="flex-1 text-lg font-semibold text-fg-strong">
                Itemization
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  id="breakdown-close-btn"
                  type="button"
                  aria-label="Close"
                  className="flex size-7 cursor-pointer items-center justify-center rounded-full bg-border-soft hover:bg-border focus-visible:outline-none"
                >
                  <X className="size-4" />
                </button>
              </Dialog.Close>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {/* Items list */}
              <div className="px-5 py-4 flex flex-col gap-3">
                <div className="flex flex-col gap-3">
                  {items.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 w-full">
                      <Input
                        id={`breakdown-item-name-${item.id}`}
                        type="text"
                        placeholder="Item name"
                        value={item.name}
                        onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                        className="flex-1 h-11 rounded-lg"
                      />
                      <Input
                        id={`breakdown-item-price-${item.id}`}
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={item.price}
                        onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                        className="w-24 h-11 text-right rounded-lg bg-[#E6E4FF]/40 border-border font-medium"
                      />
                      <button
                        id={`breakdown-item-remove-${item.id}`}
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="flex size-7 items-center justify-center rounded-full bg-danger/10 text-danger hover:bg-danger hover:text-white transition-colors shrink-0 cursor-pointer"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  id="breakdown-add-item-btn"
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center text-sm font-semibold text-info hover:underline mt-1 self-start cursor-pointer"
                >
                  {items.length > 0 ? '+ Add another item' : '+ Add item'}
                </button>
              </div>

              {/* Additional charges */}
              <div className="mt-2">
                <div className="bg-[#F1F1F1] px-5 py-2 text-sm font-semibold text-fg select-none">
                  Additional charges
                </div>
                <div className="px-5 py-4">
                  <p className="text-xs text-[#2B2B2B] font-medium italic leading-normal mb-3">
                    <strong>Note:</strong> You may add extra charges like tip or discount.
                    We&apos;ll split it fairly among the selected member.
                  </p>
                  <div className="flex flex-col gap-3">
                    {feesAndTaxes.map((fee) => (
                      <div key={fee.id} className="flex items-center gap-2 w-full">
                        {/* Name select dropdown */}
                        <div className="relative flex-1">
                          <select
                            id={`breakdown-fee-name-${fee.id}`}
                            value={fee.name}
                            onChange={(e) => updateFee(fee.id, 'name', e.target.value)}
                            className="h-11 w-full pl-4 pr-10 rounded-md border border-border-soft bg-surface-raised text-sm font-semibold text-fg appearance-none cursor-pointer focus-visible:ring-2 focus-visible:ring-ring focus:outline-none"
                          >
                            {BREAKDOWN_CHARGE_NAMES.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted pointer-events-none" />
                        </div>
                        <div className="flex items-center">
                          {/* Operator (+ / −): fixed by role for Tip/Tax/Discount; a free-sign
                              charge ("Other") makes it a toggle so the user picks add or subtract. */}
                          {chargeSignMode(fee.name) === 'free' ? (
                            <button
                              id={`breakdown-fee-sign-${fee.id}`}
                              type="button"
                              aria-label={t.newBill.toggleChargeSign}
                              onClick={() =>
                                updateFee(fee.id, 'sign', feeOperator(fee) === '-' ? '+' : '-')
                              }
                              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-l-md border border-border-soft bg-[#FFFFFF] text-base font-semibold text-fg select-none shrink-0 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {feeOperator(fee)}
                            </button>
                          ) : (
                            <div className="flex h-11 w-11 items-center justify-center rounded-l-md border border-border-soft bg-[#FFFFFF] text-base font-semibold text-fg select-none shrink-0">
                              {feeOperator(fee)}
                            </div>
                          )}
                          <Input
                            id={`breakdown-fee-price-${fee.id}`}
                            type="text"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={fee.price}
                            onChange={(e) => updateFee(fee.id, 'price', e.target.value)}
                            className="h-11 w-24 text-right font-semibold text-fg rounded-l-none rounded-r-md border border-border-soft border-l-0"
                          />
                        </div>
                        <button
                          id={`breakdown-fee-remove-${fee.id}`}
                          type="button"
                          onClick={() => removeFee(fee.id)}
                          className="flex size-7 items-center justify-center rounded-full bg-danger/10 text-danger hover:bg-danger hover:text-white transition-colors shrink-0 cursor-pointer"
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    id="breakdown-add-fee-btn"
                    type="button"
                    onClick={addFee}
                    className="inline-flex items-center text-sm font-semibold text-info hover:underline mt-2 self-start cursor-pointer"
                  >
                    + Fees and taxes
                  </button>
                </div>
              </div>
            </div>

            {/* Calculation Summary */}
            <div className="border-t border-divider bg-surface-raised px-5 py-4 flex flex-col gap-2.5 text-sm select-none">
              <div className="flex justify-between items-center text-fg">
                <span className="text-[#2B2B2B] font-medium">Receipt amount</span>
                <span className="font-semibold">
                  {currencySymbol}
                  {formatDecimalWithCommas(receiptVal.toFixed(2))}
                </span>
              </div>
              <div className="flex justify-between items-center text-fg">
                <span className="text-[#2B2B2B] font-medium">Itemized amount</span>
                <span className="font-semibold">
                  {currencySymbol}
                  {formatDecimalWithCommas(itemizedSum.toFixed(2))}
                </span>
              </div>
              <div
                className={cn(
                  'flex justify-between items-center font-semibold pt-1 border-t border-divider/50',
                  breakdownStatus.colorClass,
                )}
              >
                <span>{breakdownStatus.label}</span>
                <span>{breakdownStatus.value}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-2.5 border-t border-border-soft bg-surface-raised px-5 py-3">
              {/* Save is enabled while the breakdown is within ±0.90 of the receipt total. */}
              <Button
                id="breakdown-save-btn"
                variant="primary"
                size="md"
                onClick={handleSave}
                disabled={!canSave}
              >
                Save
              </Button>
              <Button
                id="breakdown-cancel-btn"
                variant="secondary"
                size="md"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Split-choice dialog — appears after clicking Save */}
      <SplitChoiceModal
        open={splitChoiceOpen}
        onOpenChange={setSplitChoiceOpen}
        onSplitTotal={handleSplitTotal}
        onAssignItems={handleOpenAssign}
      />

      {/* Assign-items modal — keyed on open so it REMOUNTS each time it opens, re-seeding its local
          fees/assignments from the now-populated `pendingItems`/`pendingFees` (they're empty at the
          first mount, before the Itemization step's Save fills them). */}
      <AssignItemsModal
        key={assignItemsOpen ? 'assign-open' : 'assign-closed'}
        open={assignItemsOpen}
        onOpenChange={setAssignItemsOpen}
        items={pendingItems}
        feesAndTaxes={pendingFees}
        currencySymbol={currencySymbol}
        members={members}
        onSave={(assignments, editedFees) => {
          // Persist the itemized items (drives the "View breakdown" state on the bill form) together
          // with the additional charges as edited in this step, PLUS each member's assigned amount
          // — that is what the bill's split must follow (this used to be dropped, so the expense
          // still split evenly across the whole group). Closes BOTH modals.
          onSave(
            pendingItems,
            editedFees,
            computeAssignedShares(pendingItems, editedFees, assignments),
          );
          onOpenChange(false);
        }}
      />
    </>
  );
}
